/**
 * ============================================================================
 * Website fonts — shared contract for owner-configurable typography
 * ----------------------------------------------------------------------------
 * The single source of truth for the curated font catalog AND the two
 * sanitizers that stop an owner-supplied font name / URL from breaking out of a
 * CSS declaration. This lives in `@order-up/models` (zero-dependency, imported
 * by every layer) so the renderer (`apps/restaurant`), the management builder
 * (`apps/management`) and the Zod schema (`packages/schemas`) all consume ONE
 * definition — never divergent copies. A weaker second copy of the URL guard in
 * the builder is exactly what let a hostile stored `url` inject CSS; centralize
 * here and there is only one guard to keep correct.
 * ============================================================================
 */

export type CuratedFontCategory =
    | 'brush'
    | 'display'
    | 'condensed'
    | 'serif'
    | 'sans';

export interface CuratedFontCatalogEntry {
    /** Stable id stored in `branding.fonts.*.family` for curated picks. */
    readonly id: string;
    /** Human label for the builder UI. */
    readonly label: string;
    /**
     * Plain CSS font-family NAME (e.g. `"Oswald"`, `"Playfair Display"`). The
     * renderer binds this to a `next/font` CSS variable; the builder uses it for
     * its Google-Fonts preview swatch and the `<link>` request.
     */
    readonly family: string;
    readonly category: CuratedFontCategory;
}

/**
 * The curated fonts an owner can pick, in builder display order (grouped by
 * category). Adding a font here is the ONE edit needed: TypeScript then forces
 * the renderer to register its `next/font` stack (`Record<CuratedFontId, …>`),
 * and the builder picks up the new option automatically — no drift possible.
 *
 * `id` values are the stable contract stored in `branding.fonts.*.family`; keep
 * them exact. `playfair` is the template's existing default serif.
 */
export const CURATED_FONT_CATALOG = [
    { id: 'rock-salt', label: 'Rock Salt', family: 'Rock Salt', category: 'brush' },
    {
        id: 'permanent-marker',
        label: 'Permanent Marker',
        family: 'Permanent Marker',
        category: 'brush',
    },
    { id: 'bungee', label: 'Bungee', family: 'Bungee', category: 'display' },
    { id: 'oswald', label: 'Oswald', family: 'Oswald', category: 'condensed' },
    {
        id: 'bebas-neue',
        label: 'Bebas Neue',
        family: 'Bebas Neue',
        category: 'condensed',
    },
    { id: 'anton', label: 'Anton', family: 'Anton', category: 'condensed' },
    {
        id: 'playfair',
        label: 'Playfair Display',
        family: 'Playfair Display',
        category: 'serif',
    },
    {
        id: 'merriweather',
        label: 'Merriweather',
        family: 'Merriweather',
        category: 'serif',
    },
    { id: 'aleo', label: 'Aleo', family: 'Aleo', category: 'serif' },
    { id: 'inter', label: 'Inter', family: 'Inter', category: 'sans' },
    { id: 'montserrat', label: 'Montserrat', family: 'Montserrat', category: 'sans' },
    { id: 'poppins', label: 'Poppins', family: 'Poppins', category: 'sans' },
    {
        id: 'roboto-flex',
        label: 'Roboto Flex',
        family: 'Roboto Flex',
        category: 'sans',
    },
] as const satisfies readonly CuratedFontCatalogEntry[];

/** Union of the curated font ids — the exhaustive set the renderer must cover. */
export type CuratedFontId = (typeof CURATED_FONT_CATALOG)[number]['id'];

/**
 * Strip anything that could break out of a CSS `font-family` value / `url()`,
 * so a stored custom family name can only ever NAME a font, never inject CSS.
 * The set — `" ' \ ; { } ( ) < >` — covers string-close, declaration-end,
 * rule-close, `url()`-close and tag-close characters.
 */
export function sanitizeFontFamilyName(name: string): string {
    return name.replace(/["'\\;{}()<>]/g, '').trim();
}

/**
 * A stored custom-font URL must be a bare `https://…` with NO whitespace,
 * quotes, parens, angle brackets, braces or backslash — so it can never close
 * the `url("…")` and inject a second CSS declaration. (No `m` flag, so a
 * trailing newline cannot slip past the `$` anchor.)
 */
export function isSafeFontUrl(url: string): boolean {
    return /^https:\/\/[^\s"'()<>{}\\]+$/.test(url);
}
